import { inE2E } from 'config/environment'
import { useBreakoutUser } from 'hooks/profile'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import {
  getSlideDeckReadSource,
  setSlideDeckReadSource,
} from 'util/slideDeckSource'

// Single global control, pinned to the bottom-center edge of the screen. Flips
// the per-tab sessionStorage opt-in that forces the v2 (Adonis/Postgres)
// slide-deck read instead of the default v1 cloud function, then reloads so the
// current page's cubit (student assignment or meeting) rebuilds against the new
// source. The override persists across navigations in that tab until toggled
// off (or the tab closes). Mounted once above the router, so it rides along on
// every authed route.
//
// Shown to CX (Corre) users, and in the e2e runs that actually exercise v2 (the
// `-adonis` lane sets VITE_E2E_SLIDE_DECK_SOURCE=v2, so `isV2` is true there and
// false in every other e2e run) — where it doubles as a visible, assertable
// indicator. It stays off the top-center header area so it can't intercept nav
// clicks or screenshot regions in the specs that see it. Real students never
// see it.
export const SlideDeckSourceToggle = observer(function SlideDeckSourceToggle() {
  const { isCorre } = useBreakoutUser()

  const isV2 = getSlideDeckReadSource() === 'v2'

  const toggle = useCallback(() => {
    setSlideDeckReadSource(isV2 ? undefined : 'v2')
    window.location.reload()
  }, [isV2])

  // CX can always toggle; e2e only sees it on the v2 (Adonis) runs.
  if (!isCorre && !(inE2E() && isV2)) return null

  // QA/CX label; not worth an i18n key.
  const label = `Deck source: ${isV2 ? 'v2 (Adonis)' : 'v1 (default)'}`

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="slide-deck-source-toggle"
      className="fixed bottom-0 left-1/2 z-[60] -translate-x-1/2 rounded-t-lg border border-b-0 border-black/10 bg-white px-3 py-1 text-xs font-medium text-black shadow-lg hover:bg-gray-50"
    >
      {label}
    </button>
  )
})
